/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { ThemePalette } from 'mui.theme';
import { Box } from '@mui/material';
import InfoPanel from './InfoPanel';
import LineAndCircle from './LineAndCircle';
import { useAppSelector, useAppDispatch } from 'store/hook';
import {
  convertKpaToPsi,
  getLabelWrtUnitSystem,
} from 'utils/helpers/unitSystem';
import { truncateTo2dp } from 'utils/helpers/general';
import { isOverMax } from 'views/TirePressure/Components/Content/Components/VehicleTypes/util';
import { fixedLimts } from 'views/TirePressure/util';
import { type Asset } from 'types/asset.types';
import { useNavigate } from 'react-router-dom';
import { airFilterColors } from 'views/FuelHistoricalAnalysis/utils';
import { type RuleNotifications } from 'types/notification.types';
import {
  getFuelUsageDiff,
  getNotificationIndex,
} from 'views/FleetOverview/components/AssetCardsV2/utils';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { setAssetHealthStatus } from 'store/asset.health.slice';
import { useEffect } from 'react';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

const MainBody = ({
  enableDarkTheme,
  healthData,
  unitSystem,
  makeModel,
  asset,
  notifications,
}: {
  enableDarkTheme: boolean;
  healthData: any;
  unitSystem: string;
  makeModel: string;
  asset: Asset;
  notifications: RuleNotifications[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { customerCode } = useCustomerInfo();

  const statusLabelKeys: Record<string, string> = {
    OK: 'vehicle_status_ok',
    WARNING: 'vehicle_status_warning',
    ALERT: 'vehicle_status_alert',
    UNKNOWN: 'vehicle_status_unknown',
    'NO DATA': 'vehicle_status_no_data',
    EOL: 'vehicle_status_eol',
    'APPROACHING EOL': 'vehicle_status_approaching_eol',
  };
  const statusLabel = (status: string) =>
    t(statusLabelKeys[status] ?? 'vehicle_status_ok');

  const currentCustomerInfo = useAppSelector(
    (state) => state.authReducer.customer
  );
  const { limits, fleetData } = useAppSelector(
    (state) => state.tireProfileReducer
  );
  const tirePressureData = fleetData.pressure[asset.device.deviceId] ?? {};
  const tireTempData = fleetData.temperature[asset.device.deviceId] ?? {};

  const currentLimit =
    (limits as any)?.customerLimits?.[asset.assetType] ??
    (limits as any)?.globalLimits?.[asset.assetType];

  const tireMetricDataData = (
    metricTypeData: any,
    dataType: 'pressure' | 'temperature'
  ) =>
    Object.values(metricTypeData).length === 0
      ? [{ name: 'NO DATA', color: '#e0e0e0' }]
      : Object.keys(metricTypeData).map((tireCode) => {
          const tireCodeValue = truncateTo2dp(metricTypeData[tireCode]?.value);
          const tireCodeTs = metricTypeData[tireCode]?.ts;
          let tireLimit: any = {};
          if (Array.isArray(currentLimit)) {
            tireLimit =
              currentLimit.find(
                (itemLimit: any) => itemLimit.tireCode === tireCode
              ) || {};
          } else {
            console.warn('currentLimit is not an array!', currentLimit);
          }

          // If timestamp is null, return NO DATA
          if (tireCodeTs === null) {
            return { name: 'NO DATA', color: '#e0e0e0' };
          }

          let maxValue: number, minValue: number;
          // temperature uses fixed limits from config, for pressure uses backend limits
          if (dataType === 'temperature') {
            const limitsData = (fixedLimts as any)[unitSystem][dataType];
            maxValue = limitsData?.maxValue;
            minValue = limitsData?.minValue;
          } else {
            maxValue =
              unitSystem === 'metric'
                ? tireLimit.maxPressure
                : convertKpaToPsi(tireLimit?.maxPressure);
            minValue =
              unitSystem === 'metric'
                ? tireLimit.minPressure
                : convertKpaToPsi(tireLimit?.minPressure);
          }

          const isOverMaxRes = isOverMax(
            Number(tireCodeValue),
            maxValue,
            minValue
          );

          // Handle remaining cases
          if (tireCodeValue === '--' && tireCodeTs === undefined) {
            return { name: 'NO DATA', color: '#e0e0e0' };
          } else if (tireCodeValue === 'N/A') {
            return { name: 'N/A', color: '#7D7D7D' };
          } else if (isOverMaxRes) {
            return { name: 'ALERT', color: '#EB5757' };
          } else {
            return { name: 'OK', color: '#009161' };
          }
        });

  const tirePressureDataArr = tireMetricDataData(tirePressureData, 'pressure');
  const tireTempDataArr = tireMetricDataData(tireTempData, 'temperature');

  const tireDataText = (tireDataArr: any) => {
    // Check if we have no data at all
    if (tireDataArr.length === 1 && tireDataArr[0].name === 'NO DATA') {
      return 'NO DATA';
    }

    // Check if all items are NO DATA
    if (tireDataArr.every((item: any) => item.name === 'NO DATA')) {
      return 'NO DATA';
    }

    // Count alerts
    const alertCount = tireDataArr.filter(
      (item: any) => item.name === 'ALERT'
    ).length;

    // Return status based on alert count
    if (alertCount > 2) {
      return 'ALERT';
    } else if (alertCount > 0) {
      return 'WARNING';
    } else {
      return 'OK';
    }
  };

  const pressureData = tireDataText(tirePressureDataArr);
  const tempData = tireDataText(tireTempDataArr);

  const dtc = Array.isArray(healthData?.dtc)
    ? healthData?.dtc?.filter(
        (item: any) => item.is_active && item.device === asset.device.deviceId
      )
    : [];
  const navigate = useNavigate();

  const brakingVal = healthData?.braking?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const overspeedVal = healthData?.overspeed?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const accelerationVal = healthData?.acceleration?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const gearSkipVal = healthData?.gearSkip?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const idleFuelVal = healthData?.idleFuel?.reduce(
    (
      total: number,
      item: {
        start_ts: string;
        device: string;
        customer: string;
        short_idling_fuel_consumed: number;
        long_idling_fuel_consumed: number;
        has_idle_event: boolean;
      }
    ) => {
      return (
        total + item.short_idling_fuel_consumed + item.long_idling_fuel_consumed
      );
    },
    0
  );
  const underspeedVal = healthData?.underspeed?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const incorrectGearToSpeedVal = healthData?.incorrectGearToSpeed?.find(
    (item: any) => item.device_id === asset.device.deviceId
  );
  const incorrectGearDuringDirectionVal =
    healthData?.incorrectGearDuringDirection?.find(
      (item: any) => item.device_id === asset.device.deviceId
    );
  const airFilterVal = healthData?.airFilters.some(
    (item: any) => item.filter_health === 'eol'
  )
    ? { status: 'EOL', color: airFilterColors[3] }
    : healthData?.airFilters.some(
        (item: any) => item.filter_health === 'approaching eol'
      )
    ? { status: 'APPROACHING EOL', color: airFilterColors[2] }
    : healthData?.airFilters.some(
        (item: any) => item.filter_health === 'unknown'
      )
    ? { status: 'UNKNOWN', color: airFilterColors[1] }
    : { status: 'OK', color: airFilterColors[1] };
  const notificationIndex = getNotificationIndex(notifications);

  const fuelDataVal = healthData?.fuelData.find(
    (item: any) => item.device === asset.device.deviceId
  );

  const assetDiff = getFuelUsageDiff(
    asset.fuelUsage,
    asset.utilization,
    asset.productivity,
    'day'
  );

  const pngName = getImageName(asset, 'Large');

  // Determine overall Asset Health status
  const statusesToCheck = [
    overspeedVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    underspeedVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    incorrectGearToSpeedVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    brakingVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    accelerationVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    incorrectGearDuringDirectionVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    gearSkipVal?.noOfEvents >= 1 ? 'WARNING' : 'OK',
    pressureData === 'WARNING' || pressureData === 'ALERT' ? 'WARNING' : 'OK',
    tempData === 'WARNING' || tempData === 'ALERT' ? 'WARNING' : 'OK',
    dtc.length >= 1 ? 'WARNING' : 'OK',
    notificationIndex.message !== 'No Rule-based Alerts'
      ? notificationIndex.message
      : 'OK',
    fuelDataVal ? (assetDiff?.fuelUsage < 0 ? 'OK' : 'ALERT') : 'NO_DATA',
    idleFuelVal !== undefined
      ? asset.fuelUsage?.dashboard_color !== 'green'
        ? 'WARNING'
        : 'OK'
      : 'NO_DATA',
    airFilterVal.status === 'UNKNOWN'
      ? 'UNKNOWN'
      : airFilterVal.status !== 'OK'
      ? 'WARNING'
      : 'OK',
  ];

  // Check for active issues (warnings/alerts)
  const hasActiveIssues = statusesToCheck.some((status) =>
    [
      'WARNING',
      'ALERT',
      notificationIndex.message !== 'No Rule-based Alerts',
      airFilterVal.status !== 'OK',
    ].includes(status)
  );

  // Check if ALL systems are unknown/no-data (only show Unknown when completely blind)
  const allSystemsUnknown = statusesToCheck.every(
    (status) => status === 'NO_DATA' || status === 'UNKNOWN'
  );

  const assetHealthStatus = hasActiveIssues
    ? 'Unhealthy'
    : allSystemsUnknown
    ? 'Unknown'
    : 'Healthy';

  // Dispatch the asset health status to Redux whenever it changes
  useEffect(() => {
    dispatch(
      setAssetHealthStatus({
        status: assetHealthStatus,
        assetId: asset.device.deviceId,
      })
    );
  }, [assetHealthStatus, dispatch, asset.device.deviceId]);

  console.log({ currentCustomerInfo });

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        marginTop: '20px',
        borderRadius: '5px',
      }}
    >
      {/* Truck PNG */}
      <PNG
        name={pngName}
        style={{
          width: '70%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* lines and pulsating circles */}
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      >
        {/* notifications */}
        <LineAndCircle
          x1={'32%'}
          y1={'50%'}
          x2={'10%'}
          y2={'20%'}
          r1={'10'}
          r2={'20'}
        />

        {/* tire system */}
        <LineAndCircle
          x1={'48%'}
          y1={'60%'}
          x2={'15%'}
          y2={'90%'}
          r1={'10'}
          r2={'20'}
        />

        {/* Driver Behaviour */}
        <LineAndCircle
          x1={'44%'}
          y1={'42%'}
          x2={'85%'}
          y2={'20%'}
          r1={'10'}
          r2={'20'}
        />

        {/* fuel system */}
        <LineAndCircle
          x1={'57.5%'}
          y1={'57%'}
          x2={'95%'}
          y2={'85%'}
          r1={'10'}
          r2={'20'}
        />
      </svg>

      {/* Driver Behaviour */}
      <InfoPanel
        enableDarkTheme={enableDarkTheme}
        header={t('driver_behaviour_2')}
        items={[
          {
            name: t('vehicle_status_overspeeding'),
            desc: `${overspeedVal?.noOfEvents ?? ''}`,
            status: !overspeedVal
              ? 'OK'
              : overspeedVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !overspeedVal || overspeedVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !overspeedVal || overspeedVal?.noOfEvents < 1
                ? t('vehicle_status_no_overspeeding_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_underspeeding'),
            desc: `${underspeedVal?.noOfEvents ?? ''}`,
            status: !underspeedVal
              ? 'OK'
              : underspeedVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !underspeedVal || underspeedVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !underspeedVal || underspeedVal?.noOfEvents < 1
                ? t('vehicle_status_no_underspeeding_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_incorrect_gear_to_speed_ratio'),
            desc: `${incorrectGearToSpeedVal?.noOfEvents ?? ''}`,
            status: !incorrectGearToSpeedVal
              ? 'OK'
              : incorrectGearToSpeedVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !incorrectGearToSpeedVal ||
              incorrectGearToSpeedVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !incorrectGearToSpeedVal ||
              incorrectGearToSpeedVal?.noOfEvents < 1
                ? t('vehicle_status_no_incorrect_gear_to_speed_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_hard_braking'),
            desc: `${brakingVal?.noOfEvents ?? ''}`,
            status: !brakingVal
              ? 'OK'
              : brakingVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !brakingVal || brakingVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !brakingVal || brakingVal?.noOfEvents < 1
                ? t('vehicle_status_no_hard_braking_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_hard_acceleration'),
            desc: `${accelerationVal?.noOfEvents ?? ''}`,
            status: !accelerationVal
              ? 'OK'
              : accelerationVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !accelerationVal || accelerationVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !accelerationVal || accelerationVal?.noOfEvents < 1
                ? t('vehicle_status_no_hard_acceleration_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_incorrect_gear_during_direction_switch'),
            desc: `${incorrectGearDuringDirectionVal?.noOfEvents ?? ''}`,
            status: !incorrectGearDuringDirectionVal
              ? 'OK'
              : incorrectGearDuringDirectionVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !incorrectGearDuringDirectionVal ||
              incorrectGearDuringDirectionVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !incorrectGearDuringDirectionVal ||
              incorrectGearDuringDirectionVal?.noOfEvents < 1
                ? t(
                    'vehicle_status_no_incorrect_gear_during_direction_detected'
                  )
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
          {
            name: t('vehicle_status_gear_skip'),
            desc: `${gearSkipVal?.noOfEvents ?? ''}`,
            status: !gearSkipVal
              ? 'OK'
              : gearSkipVal?.noOfEvents < 1
              ? 'OK'
              : 'WARNING',
            statusText:
              !gearSkipVal || gearSkipVal?.noOfEvents < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              navigate(`/driver-behavior/overview`, {
                state: {
                  asset: asset.bumperNumber,
                  tabIndex: 1,
                },
              });
            },
            toolTipDesc:
              !gearSkipVal || gearSkipVal?.noOfEvents < 1
                ? t('vehicle_status_no_gear_skip_detected')
                : t('vehicle_status_click_redirect_driver_behaviour'),
          },
        ]}
        sx={{
          position: 'absolute',
          top: '20%',
          right: '5%',
          transform: 'translate(0, -50%)',
        }}
      />

      {/* Tire System */}
      <InfoPanel
        enableDarkTheme={enableDarkTheme}
        header={t('tire_system')}
        items={[
          {
            name: t('vehicle_status_tpms_pressure'),
            desc: '',
            status: pressureData,
            statusText: statusLabel(pressureData),
            handleClick:
              pressureData === 'NO DATA' || pressureData === 'OK'
                ? undefined
                : () => {
                    navigate(
                      `/tire-insights/vehicle-insight?asset=${asset.bumperNumber}`,
                      {
                        state: {
                          device: asset.bumperNumber,
                          navType: 'Pressure',
                        },
                      }
                    );
                  },
            toolTipDesc:
              pressureData === 'NO DATA'
                ? ''
                : pressureData === 'OK'
                ? t('vehicle_status_tpms_pressure_normal')
                : pressureData === 'WARNING' || pressureData === 'ALERT'
                ? t('vehicle_status_click_redirect_tire_pressure')
                : '',
          },
          {
            name: t('vehicle_status_tpms_temp'),
            desc: '',
            status: tempData,
            statusText: statusLabel(tempData),
            handleClick:
              tempData === 'NO DATA' || tempData === 'OK'
                ? undefined
                : () => {
                    navigate(
                      `/tire-insights/vehicle-insight?asset=${asset.bumperNumber}`,
                      {
                        state: {
                          device: asset.bumperNumber,
                          navType: 'Temperature',
                        },
                      }
                    );
                  },
            toolTipDesc:
              tempData === 'NO DATA'
                ? ''
                : tempData === 'OK'
                ? t('vehicle_status_tpms_temperature_normal')
                : tempData === 'WARNING' || tempData === 'ALERT'
                ? t('vehicle_status_click_redirect_tire_temperature')
                : '',
          },
        ]}
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
        }}
        disabled={!currentCustomerInfo.isTiresModule}
        disabledText={
          !currentCustomerInfo.isTiresModule
            ? t('vehicle_status_tpms_not_available')
            : ''
        }
      />

      {/* Notifications */}
      <InfoPanel
        enableDarkTheme={enableDarkTheme}
        header={t('vehicle_status_notifications')}
        items={[
          {
            name: t('vehicle_status_air_filter'),
            desc: ``,
            status: `${airFilterVal.color.key}`,
            statusText: statusLabel(airFilterVal.status),
            handleClick: () => {
              if (airFilterVal.status !== 'OK' && asset.fuelUsage?.date) {
                // navigate(`/fuel-usage/historical-analysis`, {
                //   state: {
                //     device: asset.bumperNumber,
                //     date: asset.fuelUsage.date,
                //   },
                // });
              }
            },
            toolTipDesc: '',
          },
          {
            name: t('vehicle_status_oem_diagnostic_codes'),
            desc: `${dtc.length && dtc.length > 0 ? dtc.length : ''}`,
            status: `${
              !healthData || healthData === 'undefined'
                ? 'loading'
                : dtc.length < 1
                ? 'OK'
                : 'WARNING'
            }`,
            statusText:
              !healthData || healthData === 'undefined'
                ? undefined
                : dtc.length < 1
                ? statusLabel('OK')
                : statusLabel('WARNING'),
            handleClick: () => {
              if (dtc.length > 0) {
                const dataToPass = {
                  bumperNumber: asset.bumperNumber,
                };
                navigate(`/diagnostic-trouble-codes`, {
                  state: {
                    data: dataToPass,
                  },
                });
              }
            },
            toolTipDesc:
              dtc.length < 1
                ? t('vehicle_status_no_dtc_alerts')
                : t('vehicle_status_click_redirect_dtc'),
          },
          {
            name: t('vehicle_status_rule_based_alerts'),
            desc: `${
              notifications.length && notifications.length > 0
                ? notifications.length
                : ''
            }`,
            status: `${notificationIndex.color}`,
            statusText: `${
              notificationIndex.message === 'No Rule-based Alerts'
                ? statusLabel('OK')
                : notificationIndex.message
            }`,
            handleClick: () => {
              if (notifications.length > 0) {
                const dataToPass = {
                  bumperNumber: asset.bumperNumber,
                };
                navigate('/notifications', {
                  state: {
                    data: dataToPass,
                  },
                });
              }
            },
            toolTipDesc:
              notifications.length < 1
                ? t('vehicle_status_no_rule_based_alerts_tooltip')
                : t('vehicle_status_click_redirect_rule_based_notifications'),
          },
        ]}
        sx={{
          position: 'absolute',
          top: '17%',
          left: '5%',
          transform: 'translate(0, -50%)',
        }}
      />

      {/* Fuel System */}
      <InfoPanel
        enableDarkTheme={enableDarkTheme}
        header={t('fuel_system')}
        items={[
          {
            name: t('vehicle_status_total_fuel_used'),
            desc: `${
              fuelDataVal?.total_fuel_consumption != null
                ? fuelDataVal.total_fuel_consumption
                : '-'
            } ${getLabelWrtUnitSystem('L', unitSystem)}`,
            status: !fuelDataVal
              ? 'NO DATA'
              : assetDiff?.fuelUsage < 0
              ? 'OK'
              : 'ALERT',
            statusText: statusLabel(
              !fuelDataVal
                ? 'NO DATA'
                : assetDiff?.fuelUsage < 0
                ? 'OK'
                : 'ALERT'
            ),
            ...(fuelDataVal?.total_fuel_consumption != null
              ? {
                  handleClick: () => {
                    navigate(`/fuelUsage/overview`);
                  },
                  toolTipDesc:
                    !assetDiff?.fuelUsage || assetDiff.fuelUsage < 0
                      ? t('vehicle_status_click_redirect_fuel_overview')
                      : t('vehicle_status_fuel_consumption_increased'),
                }
              : {
                  toolTipDesc: t('vehicle_status_no_fuel_consumption_data'),
                }),
          },
          ...(idleFuelVal !== undefined
            ? [
                {
                  name: t('vehicle_status_idling_fuel_consumption'),
                  desc: `${
                    idleFuelVal && idleFuelVal !== 0
                      ? truncateTo2dp(idleFuelVal)
                      : '-'
                  } ${getLabelWrtUnitSystem('L', unitSystem)}`,
                  status:
                    !idleFuelVal || idleFuelVal === 0
                      ? 'NO DATA'
                      : !asset.fuelUsage?.dashboard_color ||
                        asset.fuelUsage?.dashboard_color === 'green'
                      ? 'OK'
                      : 'WARNING',
                  statusText: statusLabel(
                    !idleFuelVal || idleFuelVal === 0
                      ? 'NO DATA'
                      : !asset.fuelUsage?.dashboard_color ||
                        asset.fuelUsage?.dashboard_color === 'green'
                      ? 'OK'
                      : 'WARNING'
                  ),
                  ...(idleFuelVal &&
                  idleFuelVal !== 0 &&
                  asset.fuelUsage?.dashboard_color !== 'green'
                    ? {
                        handleClick: () => {
                          navigate(`/fuelUsage/overview`);
                        },
                        toolTipDesc: t(
                          'vehicle_status_high_idling_fuel_consumption'
                        ),
                      }
                    : {
                        toolTipDesc:
                          !idleFuelVal || idleFuelVal === 0
                            ? t(
                                'vehicle_status_no_idling_fuel_consumption_data'
                              )
                            : t(
                                'vehicle_status_idling_fuel_consumption_normal'
                              ),
                      }),
                },
              ]
            : []),
        ]}
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
        }}
      />
    </Box>
  );
};

export default MainBody;
